// 面板通話的網頁狀態（規則與 WebRTC 在 panelCallModel.ts；畫面 PanelCallOverlay.tsx，掛在 AppShell）。
//   - 登入後（AppShell 掛載）開始：聽 signaling 的 call_* 訊框 + 前景每 5 秒 GET /v3/me/calls 補漏
//   - 一次只處理一通：正在通話 / 撥出時，別的來電不跳（面板那邊 30 秒後自己結束）
//   - 拒接 / 接過 / 結束的 call_id 記在 handled（這次開著的頁面），輪詢回來不再響
//   - 鈴聲：瀏覽器規定使用者互動過才能出聲 → 沒互動過畫面上顯示明顯橫幅；響鈴時分頁標題閃爍
import { ApiError } from '../../lib/api';
import { signaling, type SignalFrame } from '../../lib/ws';
import {
  CALL_POLL_MS, CALL_RING_S, CallPeer, callAction, callOfFrame, getPanelCall, isIncomingRing, listMyCalls, outgoingStateOutcome,
  shouldRing, startPanelCall, type PanelCall,
} from './panelCallModel';

export type CallPhase = 'idle' | 'ringing' | 'calling' | 'connecting' | 'in_call' | 'ended';
export type CallEndReason = 'declined' | 'no_answer' | 'ended' | 'failed' | 'elsewhere' | 'busy';

export interface PanelCallState {
  phase: CallPhase;
  /** 目前這通（來電或撥出） */
  call: PanelCall | null;
  outgoing: boolean;
  /** 面板那邊誰接的（撥出時 call_answered.by_name）、是不是自動接聽 */
  answeredBy: string | null;
  auto: boolean;
  endReason: CallEndReason | null;
  local: MediaStream | null;
  remote: MediaStream | null;
  muted: boolean;
  /** 使用者在這個頁面互動過（瀏覽器允許出聲） */
  canRing: boolean;
  busy: boolean;
}

const EMPTY: PanelCallState = {
  phase: 'idle', call: null, outgoing: false, answeredBy: null, auto: false, endReason: null, local: null, remote: null, muted: false,
  canRing: false, busy: false,
};

let state: PanelCallState = EMPTY;
const listeners = new Set<() => void>();
const handled = new Set<string>();
let peer: CallPeer | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let outTimer: ReturnType<typeof setTimeout> | null = null;
let outPoll: ReturnType<typeof setInterval> | null = null;
let endTimer: ReturnType<typeof setTimeout> | null = null;
let offMsg: (() => void) | null = null;
let generation = 0;
let ringer: { stop: () => void } | null = null;
let titleTimer: ReturnType<typeof setInterval> | null = null;
let savedTitle: string | null = null;

function set(patch: Partial<PanelCallState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

const nowS = () => Math.floor(Date.now() / 1000);
/** 讀「現在」的狀態（await 之後別的事件可能已經改了；直接寫 state.phase 會被 TS 當成 await 前縮窄過的值） */
const phaseNow = (): CallPhase => state.phase;
const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

// ---- 鈴聲與標題閃爍 ----

function startRinging(title: string) {
  stopRinging();
  if (state.canRing) ringer = beep();
  if (typeof document !== 'undefined') {
    savedTitle = document.title;
    let on = false;
    titleTimer = setInterval(() => {
      on = !on;
      document.title = on ? `📞 ${title}` : savedTitle ?? '';
    }, 1_000);
  }
}

function stopRinging() {
  ringer?.stop();
  ringer = null;
  if (titleTimer) clearInterval(titleTimer);
  titleTimer = null;
  if (savedTitle !== null && typeof document !== 'undefined') document.title = savedTitle;
  savedTitle = null;
}

/** 兩聲一組的鈴聲（WebAudio；不用音檔）。瀏覽器不准 → 安靜失敗 */
function beep(): { stop: () => void } | null {
  try {
    const Ctx = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    const tone = () => {
      for (const at of [0, 0.4]) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.frequency.value = 880;
        g.gain.value = 0.15;
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + at);
        o.stop(ctx.currentTime + at + 0.25);
      }
    };
    tone();
    const t = setInterval(tone, 2_000);
    return { stop: () => { clearInterval(t); void ctx.close().catch(() => {}); } };
  } catch {
    return null;
  }
}

function onInteract() {
  if (state.canRing) return;
  set({ canRing: true });
  if (state.phase === 'ringing' && !ringer) ringer = beep();
}

// ---- 結束 / 清理 ----

function clearTimers() {
  if (outTimer) clearTimeout(outTimer);
  if (outPoll) clearInterval(outPoll);
  outTimer = null;
  outPoll = null;
}

function stopMedia() {
  peer?.stop();
  peer = null;
  state.local?.getTracks().forEach((t) => t.stop());
}

function finish(reason: CallEndReason) {
  const c = state.call;
  if (c) handled.add(c.call_id);
  stopRinging();
  clearTimers();
  stopMedia();
  set({ phase: 'ended', endReason: reason, local: null, remote: null, busy: false });
  if (endTimer) clearTimeout(endTimer);
  endTimer = setTimeout(() => panelCall.dismiss(), 4_000);
}

async function getMedia(): Promise<MediaStream | null> {
  try {
    return (await navigator.mediaDevices?.getUserMedia({ video: true, audio: true })) ?? null;
  } catch {
    try {
      return (await navigator.mediaDevices?.getUserMedia({ audio: true })) ?? null;   // 沒鏡頭 / 不給鏡頭 → 只用聲音
    } catch {
      return null;   // 都不給 → 只看 / 聽對方
    }
  }
}

function startPeer(callId: string) {
  if (peer) return;
  set({ phase: 'connecting' });
  peer = new CallPeer({
    callId,
    send: (f) => signaling.sendRaw(f),
    local: state.local,
    onRemote: (s) => set({ remote: s }),
    onConnected: () => set({ phase: 'in_call' }),
    onFailed: () => finish('failed'),
  });
  peer.setMuted(state.muted);
  peer.start();
}

// ---- 收訊框 / 輪詢 ----

function ring(c: PanelCall) {
  if (state.phase !== 'idle' && state.phase !== 'ended') return;   // 正在通話 / 撥出：不跳
  if (!shouldRing(c, handled, nowS())) return;
  if (endTimer) clearTimeout(endTimer);
  set({ ...EMPTY, canRing: state.canRing, phase: 'ringing', call: c, outgoing: false });
  startRinging(c.caller_name || c.panel_name);
  // 30 秒後還在響 → 收起（面板那邊也結束）
  outTimer = setTimeout(() => {
    if (state.phase === 'ringing' && state.call?.call_id === c.call_id) finish('no_answer');
  }, Math.max(1, CALL_RING_S - (c.at ? nowS() - c.at : 0)) * 1000);
}

function onFrame(f: SignalFrame) {
  const id = typeof f.call_id === 'string' ? f.call_id : (f.payload as Record<string, unknown> | undefined)?.call_id;
  if (f.type === 'call_ring') {
    if (isIncomingRing(f)) {
      const c = callOfFrame(f);
      if (c) ring(c);
    }
    return;
  }
  const cur = state.call;
  if (!cur || id !== cur.call_id) return;
  const pl = { ...(f.payload ?? {}), ...f } as Record<string, unknown>;
  switch (f.type) {
    case 'call_answered':
      if (state.outgoing && state.phase === 'calling') {
        set({ answeredBy: typeof pl.by_name === 'string' && pl.by_name ? pl.by_name : null, auto: pl.auto === true });
        clearTimers();
        startPeer(cur.call_id);
      } else if (!state.outgoing && state.phase === 'ringing') finish('elsewhere');   // 別台（家人另一支手機）先接了
      break;
    case 'call_declined':
      finish(state.outgoing ? 'declined' : 'elsewhere');
      break;
    case 'call_end':
      if (state.phase !== 'ended') finish(state.phase === 'ringing' && !state.outgoing ? 'no_answer' : 'ended');
      break;
    case 'call_ready':
    case 'call_answer':
    case 'call_ice':
    case 'error':
      peer?.handle(f);
      break;
  }
}

async function poll(): Promise<void> {
  if (hidden() || state.phase !== 'idle') return;
  const gen = generation;
  try {
    const list = await listMyCalls();
    if (gen !== generation) return;
    const c = list.find((x) => shouldRing(x, handled, nowS()));
    if (c) ring(c);
  } catch {
    /* 讀不到：下一輪再試 */
  }
}

const onVisible = () => {
  if (!hidden()) void poll();
};

export const panelCall = {
  get: (): PanelCallState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /** 登入後開始（AppShell 掛載）；回傳停止函式 */
  start(): () => void {
    panelCall.stop();
    generation++;
    offMsg = signaling.on('message', onFrame);
    void poll();
    pollTimer = setInterval(() => void poll(), CALL_POLL_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
      window.addEventListener('pointerdown', onInteract);
      window.addEventListener('keydown', onInteract);
    }
    return () => panelCall.stop();
  },

  stop(): void {
    generation++;
    offMsg?.();
    offMsg = null;
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
    if (endTimer) clearTimeout(endTimer);
    endTimer = null;
    clearTimers();
    stopRinging();
    stopMedia();
    handled.clear();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
    }
    state = EMPTY;
    listeners.forEach((l) => l());
  },

  /** 立刻讀一次（測試用） */
  refresh: poll,

  /** ［接聽］：先拿鏡頭 / 麥克風（使用者按的，瀏覽器允許）→ answer → call_join → call_ready 後送 offer */
  async answer(): Promise<void> {
    const c = state.call;
    if (!c || state.phase !== 'ringing' || state.busy) return;
    stopRinging();
    set({ busy: true });
    const gen = generation;
    try {
      await callAction(c.call_id, 'answer');
      if (gen !== generation) return;
      handled.add(c.call_id);
      clearTimers();
      const local = await getMedia();
      if (gen !== generation || state.call?.call_id !== c.call_id) {
        local?.getTracks().forEach((t) => t.stop());
        return;
      }
      set({ local, busy: false });
      startPeer(c.call_id);
    } catch (e) {
      if (gen !== generation) return;
      // 404 / 409：別台先接 / 已結束
      finish(e instanceof ApiError && (e.status === 404 || e.status === 409) ? 'elsewhere' : 'failed');
    }
  },

  /** ［拒接］ */
  async decline(): Promise<void> {
    const c = state.call;
    if (!c || state.phase !== 'ringing') return;
    handled.add(c.call_id);
    stopRinging();
    set({ ...EMPTY, canRing: state.canRing });
    await callAction(c.call_id, 'decline').catch(() => {});
  },

  /** ［掛斷］（撥出中 / 通話中） */
  async hangup(): Promise<void> {
    const c = state.call;
    if (!c || state.phase === 'idle' || state.phase === 'ended') return;
    finish('ended');
    if (c.call_id) await callAction(c.call_id, 'end').catch(() => {});   // 撥出還沒拿到 call_id：dial 那邊看到已結束會補送 end
  },

  /** ［打給這台面板］ */
  async dial(panelId: string, panelName: string, callerName?: string): Promise<void> {
    if (state.phase !== 'idle' && state.phase !== 'ended') return;
    if (endTimer) clearTimeout(endTimer);
    const gen = generation;
    const pending: PanelCall = { call_id: '', panel_device_id: panelId, panel_name: panelName, ...(callerName ? { caller_name: callerName } : {}), state: 'calling', at: nowS(), reason: '' };
    set({ ...EMPTY, canRing: true, phase: 'calling', call: pending, outgoing: true, busy: true });
    const local = await getMedia();   // 按鈕按下去的同一個動作裡先問權限
    if (gen !== generation || phaseNow() !== 'calling') {
      local?.getTracks().forEach((t) => t.stop());
      return;
    }
    set({ local });
    try {
      const c = await startPanelCall(panelId);
      if (gen !== generation || phaseNow() !== 'calling') {
        void callAction(c.call_id, 'end').catch(() => {});
        return;
      }
      set({ call: { ...c, panel_name: c.panel_name || panelName, ...(c.caller_name || callerName ? { caller_name: c.caller_name || callerName } : {}) }, busy: false });
      handled.add(c.call_id);
      // 30 秒沒接 → 沒有接（雲端那邊也會結束；這裡送 end 保險）
      outTimer = setTimeout(() => {
        if (phaseNow() === 'calling' && state.call?.call_id === c.call_id) {
          finish('no_answer');
          void callAction(c.call_id, 'end').catch(() => {});
        }
      }, CALL_RING_S * 1000);
      // signaling 漏了 call_answered / call_declined → 輪詢補
      outPoll = setInterval(() => {
        if (phaseNow() !== 'calling' || state.call?.call_id !== c.call_id) return;
        void getPanelCall(c.call_id).then((r) => {
          if (!r || phaseNow() !== 'calling' || state.call?.call_id !== c.call_id) return;
          const o = outgoingStateOutcome(r.state);
          if (o === 'answered') { clearTimers(); startPeer(c.call_id); }
          else if (o === 'declined') finish('declined');
          else if (o === 'ended') finish('no_answer');
        }).catch(() => {});
      }, CALL_POLL_MS);
    } catch (e) {
      if (gen !== generation) return;
      finish(e instanceof ApiError && e.status === 409 ? 'busy' : 'failed');
    }
  },

  toggleMute(): void {
    const muted = !state.muted;
    peer?.setMuted(muted);
    set({ muted });
  },

  /** 結束提示關掉 → 回到沒事 */
  dismiss(): void {
    if (state.phase !== 'ended') return;
    if (endTimer) clearTimeout(endTimer);
    endTimer = null;
    set({ ...EMPTY, canRing: state.canRing });
  },
};

/** 測試用 */
export function __resetPanelCallForTest(): void {
  panelCall.stop();
}
